import React, { useState, useMemo } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { Button, ButtonToolbar, DropdownButton, MenuItem } from 'react-bootstrap';
import { isNull } from 'lodash';
import PropTypes from 'prop-types';

import { setPageFiltersQuery } from '../../../../../redux/actions/reports';
import { useWindowSize } from '../../../../../utils/hooks/useWindowSize';
import SubMenuWithDate from '../../../Payments/Chart/components/Filters/SubMenuWithDate';
import VirtualSelect from '../../../../../components/Select/Virtual';
import SearchInput from '../../../../../components/Search/SearchInput';
import {
  getObjectCategories,
  getObjectProviders,
  getOptionsCategories,
  getOptionsProviders,
  getOptionsStatuses,
} from '../../helpers';
import { DEFAULT_FILTERS } from '../../constants';

const searchTypes = [
  {
    value: 1,
    name: 'Service name',
  },
  {
    value: 2,
    name: 'Service ID',
  },
]

const Filters = ({ filters }) => {
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const reports = useSelector((state) => state.reports);
  const { pageFilters, pageData, isLoading, pageFiltersQuery } = reports;

  const providersObject = useMemo(() => getObjectProviders(pageData.providers), [pageData]);
  const optionsProviders = useMemo(() => getOptionsProviders(pageData.reports, providersObject, pageFiltersQuery), [pageData, providersObject, pageFiltersQuery]);
  const optionsStatuses = useMemo(() => getOptionsStatuses(pageData.reports, pageFiltersQuery), [pageData, pageFiltersQuery]);

  const categoriesObject = useMemo(() => getObjectCategories(pageData.reports), [pageData]);
  const optionsCategories = useMemo(() => getOptionsCategories(pageData.reports, categoriesObject, pageFiltersQuery), [pageData, categoriesObject, pageFiltersQuery]);

  const [form, setForm] = useState(DEFAULT_FILTERS)
  const { status, range, date_from, date_to, categories, providers } = form;

  const activeStatus = useMemo(() => optionsStatuses.find((item) => item.id === status), [status, optionsStatuses])

  const [showDrop, setShowDrop] = useState(false);
  const activeFilter = useMemo(() => filters?.date_range_filter?.find((item) => item.code === range), [range, filters]);

  const onSelectItem = (key, date) => {
    if (key === 'custom') {
      setShowDrop(false);
    }
    let newQuery = {};
    if (key === 'custom') {
      newQuery = { range: key, date_from: date?.startDate, date_to: date?.endDate };
    } else {
      newQuery = { date_from: null, date_to: null, range: key };
    }

    setForm((prevState) => ({
      ...prevState,
      ...newQuery,
    }));
  };

  const onChangeForm = (key, value) => {
    setForm((prevState) => ({
      ...prevState,
      [key]: value,
    }))
  }

  const onSubmitQuery = (e) => {
    e.preventDefault();
    const payload = {
      ...pageFilters,
      query: form.query,
      query_type: form.query_type,
      range,
      date_from,
      status,
      date_to,
      categories,
      providers,
    };
    dispatch(setPageFiltersQuery(payload))
  }

  return (
    <div>
      <div className="reports-filters__items mb-15">
        <div className="reports-filters__items-block">
          <ButtonToolbar>
            <DropdownButton
              disabled={isLoading}
              onToggle={setShowDrop}
              open={showDrop}
              pullRight={width > 767}
              bsStyle="default"
              title={activeFilter?.title || 'Today'}
              id="reports-dropdown-selectrange"
            >
              {filters?.date_range_filter?.map((item) => {
                if (item.code === 'custom') {
                  return (
                    <SubMenuWithDate
                      key={item.code}
                      element={item}
                      filters={{
                        date_range_filter_settings: {
                          default_from: date_from || filters?.date_range_filter_settings?.default_from,
                          default_to: date_to || filters?.date_range_filter_settings?.default_to,
                        },
                      }}
                      pullRight={width > 767}
                      minDate={filters?.date_range_filter_settings?.min_date}
                      maxDate={filters?.date_range_filter_settings?.max_date}
                      onSelect={onSelectItem}
                    />
                  );
                }
                return (
                  <MenuItem
                    key={item.code}
                    active={item.code === activeFilter?.code}
                    eventKey={item.code}
                    onSelect={onSelectItem}
                  >
                    {item.title}
                  </MenuItem>
                );
              })}
            </DropdownButton>
          </ButtonToolbar>
        </div>

        <div className="reports-filters__items-block">
          <VirtualSelect
            disabled={isLoading}
            toggleBtnTitle="Categories"
            optionsTitle="categories"
            onChange={(options) => onChangeForm('categories', options)}
            options={optionsCategories}
            selectedOptions={form.categories}
            menuWidth={340}
            showLoader={false}
            toggleComponent={({ values }) => (
              <>
                {values?.length === 0 ? (
                  'All categories'
                ) : (
                  <>
                    Categories
                    {' '}
                    <span className="options-count">{`(${values.length})`}</span>
                  </>
                )}
              </>
            )}
            needUpdateSelected
          />
        </div>

        <div className="reports-filters__items-block">
          <VirtualSelect
            disabled={isLoading}
            toggleBtnTitle="Providers"
            optionsTitle="providers"
            onChange={(options) => onChangeForm('providers', options)}
            options={optionsProviders}
            selectedOptions={form.providers}
            menuWidth={310}
            showLoader={false}
            toggleComponent={({ values }) => (
              <>
                {values?.length === 0 ? (
                  'All providers'
                ) : (
                  <>
                    Providers
                    {' '}
                    <span className="options-count">{`(${values.length})`}</span>
                  </>
                )}
              </>
            )}
            needUpdateSelected
          />
        </div>

        <div className="reports-filters__items-block">
          <DropdownButton
            disabled={isLoading}
            bsStyle="default"
            title={isNull(status) ? 'All statuses' : activeStatus.title}
            id="dropdown-filter-status"
          >
            {optionsStatuses.map((status) => (
              <MenuItem
                key={status.id}
                eventKey={status.id}
                active={status.id === form.status}
                onClick={() => onChangeForm('status', status.id)}
              >
                {status.title}
                {' '}
                (
                {status.count}
                )
              </MenuItem>
            ))}
          </DropdownButton>
        </div>

        <div className="reports-filters__items-block">
          <Button
            disabled={isLoading}
            bsStyle="primary"
            onClick={onSubmitQuery}
          >
            Submit
          </Button>
        </div>
      </div>
      <ul className="nav nav-tabs reports-filters__items-block-search">
        <SearchInput
          disabled={isLoading}
          onChange={(e) => onChangeForm('query', e.target.value)}
          onChangeOption={(e) => onChangeForm('query_type', e.target.value)}
          value={form.query}
          optionSelected={form.query_type}
          placeholder="Search"
          options={searchTypes}
          onSubmit={onSubmitQuery}
          showButton
        />
      </ul>
    </div>
  );
};

Filters.propTypes = {
  filters: PropTypes.object,
};

export default Filters;
